import React, { ReactNode, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useHistory, useLocation } from 'react-router-dom'

import { LOGIN_PATH } from 'account/login/Login'
import { Layout as AntLayout, Menu } from 'antd'

import { User } from 'core/application/account/loginUser'
import { Permissions } from 'core/application/account/permissions'

import HeaderUserSection from 'common/components/layout/HeaderUserSection'
import InternalToolsHeaderSwitch from 'common/components/navigation/InternalToolsHeaderSwitch/InternalToolsHeaderSwitch'

import { ROUTE_PATHS } from '../common/routePaths'
import { AI_ANALYTICS_PATH } from './AiAnalytics'
import { EXPENSES_PATH } from './Expenses'
import { HOME_PATH } from './Home'
import { KEYWORD_MANAGEMENT } from './KeywordManagement'
import { PREMIUMS_PATH } from './Premiums'
import { SEARCH_EXPENSES_PATH } from './searchExpenses'

import './DocReviewDashboard.less'

const { SubMenu } = Menu

interface AdminDashboardProps {
  children?: ReactNode
  user: User
}

export interface MenuItem {
  name: string
  key: number
  path: string
  icon?: string
  component: any
}

export enum NavigationMapping {
  Home = 1,
  Premiums = 2,
  Expenses = 3,
  SearchExpenses = 4,
  CannedMessages = 5,
  ChangePassword = 6,
  SearchPremiums = 7,
  AiAnalytics = 8,
  AiAnalyticsJson = 9,
  KeywordManagement = 10,
}

export const navigationPathMapping = new Map<string, number>([
  [HOME_PATH, NavigationMapping.Home],
  [PREMIUMS_PATH, NavigationMapping.Premiums],
  [EXPENSES_PATH, NavigationMapping.Expenses],
  [SEARCH_EXPENSES_PATH, NavigationMapping.SearchExpenses],
  ['canned-messages', NavigationMapping.CannedMessages],
  [AI_ANALYTICS_PATH, NavigationMapping.AiAnalytics],
  [KEYWORD_MANAGEMENT, NavigationMapping.KeywordManagement],
])

const DocReviewDashboardWrapper: React.FC<AdminDashboardProps> = ({ children, user }) => {
  let history = useHistory()
  let location = useLocation()
  const [activeKey, setActiveKey] = useState<string[] | undefined>()
  const { t: translate } = useTranslation()

  const goToPath = (path: string) => {
    history.push(path)
  }

  useEffect(() => {
    if (!isUserAllowedToEnterDashbaord(user)) history.push(LOGIN_PATH)
  }, [])

  const isUserAllowedToEnterDashbaord = (user: User): boolean => {
    return user !== null && user.permissions.includes(Permissions.AccessDocReviewDashboard)
  }

  useEffect(() => {
    let pathname = location.pathname
    const split = pathname.split('/')[2]
    const res = navigationPathMapping.get(split)
    setActiveKey(res ? [res.toString()] : [NavigationMapping.Home.toString()])
  }, [location])

  const userSection = (
    <Menu.Item key={11} onClick={() => {}} className="header_right_section  mr-64 no_hover_styles ">
      <div className="flex__space_between flex__space_between_centered ">
        <HeaderUserSection user={user} userInfo={user} />
      </div>
    </Menu.Item>
  )

  return (
    <AntLayout>
      <Menu selectedKeys={activeKey} mode="horizontal">
        <InternalToolsHeaderSwitch defaultValue={Permissions.AccessDocReviewDashboard} />
        <Menu.Item
          key={NavigationMapping.Home}
          onClick={() => goToPath(`${ROUTE_PATHS.DOC_REVIEW_DASHBOARD_PATH}/${HOME_PATH}`)}
        >
          {translate('doc-review-dashboard.menu.home')}
        </Menu.Item>
        <SubMenu title={translate('doc-review-dashboard.menu.premiums')}>
          <Menu.Item key={NavigationMapping.Premiums} onClick={() => goToPath(ROUTE_PATHS.DOC_REVIEW_PREMIUMS)}>
            {translate('doc-review-dashboard.menu.premiums')}
          </Menu.Item>
          <Menu.Item
            key={NavigationMapping.SearchPremiums}
            onClick={() => goToPath(ROUTE_PATHS.DOC_REVIEW_SEARCH_PREMIUMS)}
          >
            Search Premiums
          </Menu.Item>
        </SubMenu>

        <SubMenu title={translate('doc-review-dashboard.menu.expenses')}>
          <Menu.Item
            key={NavigationMapping.Expenses}
            onClick={() => goToPath(`${ROUTE_PATHS.DOC_REVIEW_DASHBOARD_PATH}/${EXPENSES_PATH}`)}
          >
            {translate('doc-review-dashboard.menu.expenses')}
          </Menu.Item>
          <Menu.Item
            key={NavigationMapping.SearchExpenses}
            onClick={() => goToPath(`${ROUTE_PATHS.DOC_REVIEW_DASHBOARD_PATH}/${SEARCH_EXPENSES_PATH}`)}
          >
            {translate('doc-review-dashboard.menu.search-expenses')}
          </Menu.Item>
        </SubMenu>
        <Menu.Item
          key={NavigationMapping.CannedMessages}
          onClick={() => goToPath(`${ROUTE_PATHS.DOC_REVIEW_DASHBOARD_PATH}/canned-messages`)}
        >
          {translate('doc-review-dashboard.menu.canned-messages')}
        </Menu.Item>
        <Menu.Item
          key={NavigationMapping.AiAnalytics}
          onClick={() => goToPath(`${ROUTE_PATHS.DOC_REVIEW_DASHBOARD_PATH}/${AI_ANALYTICS_PATH}`)}
        >
          {translate('doc-review-dashboard.menu.ai-analytics')}
        </Menu.Item>
        <Menu.Item
          key={NavigationMapping.KeywordManagement}
          onClick={() => goToPath(`${ROUTE_PATHS.DOC_REVIEW_DASHBOARD_PATH}/${KEYWORD_MANAGEMENT}`)}
        >
          {translate('doc-review-dashboard.menu.keyword-management')}
        </Menu.Item>
        {userSection}
      </Menu>
      <AntLayout className="dashboard_body">
        <AntLayout className="dashboard-layout-content">{children}</AntLayout>
      </AntLayout>
    </AntLayout>
  )
}

export default DocReviewDashboardWrapper
